import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { bind } from '../Utils/Bind';
import { LoadGoogleMapScript } from './LoadGoogleMapScript';

export interface ILocationSearchInputProps {
    onSelect?(address: string, lat: number, lng: number, geocoderResult: google.maps.GeocoderResult): void;
    readonly value: string;
    readonly disabled?: boolean;
}

@observer
export class LocationSearchInput extends React.Component<ILocationSearchInputProps> {
    public constructor(props: ILocationSearchInputProps) {
        super(props);

        this.fieldValue = props.value;
    }

    public componentDidMount() {
        try {
            this.onSelect(this.props.value);
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        const dropdownClass = 'autocomplete-dropdown-container';
        return (
            <PlacesAutocomplete value={this.value} onChange={this.onChange} onSelect={this.onSelect}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                        <input
                            {...getInputProps({
                                placeholder: 'Search Places ...',
                                className: 'form-control',
                            })}
                            disabled={this.props.disabled}
                        />
                        <div className={suggestions.length > 0 ? dropdownClass + ' border' : dropdownClass}>
                            {loading && <div>Loading...</div>}
                            {suggestions.map(suggestion => {
                                const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                                // inline style for demonstration purpose
                                const style = suggestion.active
                                    ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                                    : { backgroundColor: '#ffffff', cursor: 'pointer' };
                                return (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            className,
                                            style,
                                        })}
                                        key={suggestion.index}
                                    >
                                        <span>{suggestion.description}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </PlacesAutocomplete>
        );
    }

    @bind
    @action
    private onChange(value: string) {
        this.editing = true;
        this.fieldValue = value;
    }

    @bind
    @action
    private async onSelect(address: string) {
        let geoCoderResult: google.maps.GeocoderResult[] | undefined;
        let latLng: google.maps.LatLngLiteral | undefined;

        try {
            const response = await fetch(`/api/v1/google-maps/geocode-address?address=${encodeURIComponent(address)}`);
            const data = await response.json();

            if (data.status === 'OK') {
                geoCoderResult = data.results;
                latLng = {
                    lat: data.results[0].geometry.location.lat,
                    lng: data.results[0].geometry.location.lng,
                };
            } else {
                console.warn('Geocoding failed:', data.status);
                return;
            }
        } catch (e: any) {
            console.error(e);
            return;
        }

        if (this.props.onSelect) this.props.onSelect(address, latLng.lat, latLng.lng, geoCoderResult![0]);

        this.editing = false;
    }

    @computed
    private get value() {
        return this.editing ? this.fieldValue : this.props.value;
    }

    @observable
    private editing: boolean = false;

    @observable
    private fieldValue: string;
}
